import { createContext, useContext, type ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';

const SchemeContext = createContext<ResearchScheme>('paper');

export type ResearchScheme = 'paper' | 'ink' | 'sun' | 'fjord';

/** Most series a scheme can show; more than that is a table, not a chart. */
export const RESEARCH_MAX_SERIES: Record<ResearchScheme, number> = { paper: 6, ink: 6, sun: 3, fjord: 4 };

export type ResearchPanelProps = {
  scheme?: ResearchScheme;
  /** States the finding, not the chart type ("Three in four Helsinki jobs are fully on-site"). */
  headline: ReactNode;
  children?: ReactNode;
  /** Index date and sample, required ("JobCrawls index, 11 Oct 2026. 4,175 open jobs in Helsinki."). */
  source: ReactNode;
  /** Heading level of the headline in the page outline. */
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

/**
 * One research or data-post piece in one of the four research colour schemes (Paper by default).
 * The scheme is fixed for the piece and ignores the site's light/dark mode; children colour data
 * with the jc-r-* roles (styles/jc-research-schemes.css). One scheme per piece, never mixed.
 */
export function ResearchPanel({
  scheme = 'paper',
  headline,
  children,
  source,
  headingLevel = 3,
  className,
}: ResearchPanelProps) {
  const Heading = `h${headingLevel}` as 'h3';
  return (
    <SchemeContext.Provider value={scheme}>
      <figure
        data-jc-research={scheme}
        className={cx('m-0 flex flex-col gap-3 rounded-jc-container bg-jc-r-ground p-5 text-jc-r-text', className)}
      >
        <Heading className="type-subheading">{headline}</Heading>
        {children}
        <figcaption className="mt-auto pt-1 text-xs text-jc-r-muted">{source}</figcaption>
      </figure>
    </SchemeContext.Provider>
  );
}

export type ResearchSeries = 1 | 2 | 3 | 4 | 5 | 6 | 'highlight' | 'context' | 'remote' | 'hybrid' | 'onsite';

const FILL: Record<string, string> = {
  1: 'bg-jc-r-1',
  2: 'bg-jc-r-2',
  3: 'bg-jc-r-3',
  4: 'bg-jc-r-4',
  5: 'bg-jc-r-5',
  6: 'bg-jc-r-6',
  highlight: 'bg-jc-r-highlight',
  context: 'bg-jc-r-context',
  remote: 'bg-jc-r-remote',
  hybrid: 'bg-jc-r-hybrid',
  onsite: 'bg-jc-r-onsite',
};
export const researchFill = (series: ResearchSeries): string => FILL[String(series)];

export type SplitSegment = {
  id: string;
  label: ReactNode;
  value: number;
  /** Formatted value. */ display: ReactNode;
  series: ResearchSeries;
};

export type ResearchSplitBarProps = {
  segments: SplitSegment[];
  /** One sentence for screen readers with every share. */
  description: string;
  className?: string;
};

/** A whole split into parts (work model of open jobs), labelled directly underneath. */
export function ResearchSplitBar({ segments, description, className }: ResearchSplitBarProps) {
  const scheme = useContext(SchemeContext);
  devWarn(
    segments.length > RESEARCH_MAX_SERIES[scheme],
    `ResearchSplitBar has ${segments.length} parts; ${scheme} holds at most ${RESEARCH_MAX_SERIES[scheme]}. More than that is a table.`,
  );
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0) || 1;
  return (
    <div className={cx('flex flex-col gap-2', className)}>
      <div role="img" aria-label={description} className="flex h-6 gap-0.5 overflow-hidden rounded-jc-info">
        {segments.map((s) => (
          <span
            key={s.id}
            className={researchFill(s.series)}
            style={{ flexGrow: Math.max(0, s.value) / total, flexBasis: 0 }}
          />
        ))}
      </div>
      <ul aria-hidden="true" className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
        {segments.map((s) => (
          <li key={s.id} className="flex items-center gap-1.5">
            <span className={cx('size-2.5 rounded-jc-info', researchFill(s.series))} />
            {s.label}
            <span className="font-display font-bold tabular-nums">{s.display}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export type BarRow = { id: string; label: ReactNode; value: number; /** Formatted value. */ display: ReactNode };

export type ResearchBarListProps = {
  rows: BarRow[];
  /** The one row the headline is about: it gets the highlight colour and every other row context. Without it every row is highlight. */
  highlightId?: string;
  className?: string;
};

/** Ranked bars on one shared scale, labelled directly; a real table twin for screen readers. */
export function ResearchBarList({ rows, highlightId, className }: ResearchBarListProps) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className={className}>
      <ul aria-hidden="true" className="flex flex-col gap-2 text-xs font-semibold">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center gap-2">
            <span className="w-2/5 shrink-0 truncate">{r.label}</span>
            <span className="h-3 min-w-0 flex-1 overflow-hidden rounded-jc-info bg-jc-r-track">
              <span
                className={cx(
                  'block h-full rounded-jc-info',
                  researchFill(highlightId && r.id !== highlightId ? 'context' : 'highlight'),
                )}
                style={{ width: `${(Math.max(0, r.value) / max) * 100}%` }}
              />
            </span>
            <span className="shrink-0 text-right font-display font-bold whitespace-nowrap tabular-nums">
              {r.display}
            </span>
          </li>
        ))}
      </ul>
      <table className="sr-only">
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <th scope="row">{r.label}</th>
              <td>{r.display}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
